import { InlineNotification, Modal, TextInput } from '@carbon/react';
import { DECIMAL_FORMATTER_THREE_FRACTIONS } from 'lib/ui';
import { useEffect, useState } from 'react';
import { StateTaxesQueryResponse } from 'types/api-responses';
import styles from './state-taxes.module.scss';
import { UpdateStateTaxPayload, useUpdateStateTax } from './utils';
type FormValues = {
  stateAbbreviation: string;
  oilTaxPercentage: string;
  gasTaxPercentage: string;
  nglTaxPercentage: string;
  adValoremTaxPercentage: string;
};

interface Props {
  open: boolean;
  stateTax: StateTaxesQueryResponse | null;
  onClose: () => void;
  onSaved: () => void;
  setUpdateFailedError: (error: boolean) => void;
  updateFailedError: boolean;
}

export const EditStateTaxModal = ({
  open,
  stateTax,
  onClose,
  onSaved,
  setUpdateFailedError,
  updateFailedError,
}: Props) => {
  const [formValues, setFormValues] = useState<FormValues>({
    stateAbbreviation: '',
    oilTaxPercentage: '',
    gasTaxPercentage: '',
    nglTaxPercentage: '',
    adValoremTaxPercentage: '',
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormValues, string>>
  >({});
  const [isDirty, setIsDirty] = useState(false);

  const updateStateTax = useUpdateStateTax();

  // Reset form when modal opens with new state tax data
  useEffect(() => {
    if (stateTax) {
      setFormValues({
        stateAbbreviation: stateTax.stateAbbreviation,
        oilTaxPercentage:
          stateTax.oilTaxPercentage !== null
            ? DECIMAL_FORMATTER_THREE_FRACTIONS.format(
                stateTax.oilTaxPercentage
              )
            : '',
        gasTaxPercentage:
          stateTax.gasTaxPercentage !== null
            ? DECIMAL_FORMATTER_THREE_FRACTIONS.format(
                stateTax.gasTaxPercentage
              )
            : '',
        nglTaxPercentage:
          stateTax.nglTaxPercentage !== null
            ? DECIMAL_FORMATTER_THREE_FRACTIONS.format(
                stateTax.nglTaxPercentage
              )
            : '',
        adValoremTaxPercentage:
          stateTax.adValoremTaxPercentage !== null
            ? DECIMAL_FORMATTER_THREE_FRACTIONS.format(
                stateTax.adValoremTaxPercentage
              )
            : '',
      });
      setErrors({});
      setIsDirty(false);
    }
  }, [stateTax, open]);

  const validateField = (name: keyof FormValues, value: string): string => {
    if (!value) return 'Required';
    if (!/^\d*(\.\d+)?$/.test(value)) return 'Must be a valid number';
    if (
      [
        'oilTaxPercentage',
        'gasTaxPercentage',
        'nglTaxPercentage',
        'adValoremTaxPercentage',
      ].includes(name)
    ) {
      if (value && !/^\d*\.?\d{0,3}$/.test(value)) {
        return 'Maximum three decimal places allowed';
      }
    }
    return '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);

    if (name !== 'stateAbbreviation') {
      const error = validateField(name as keyof FormValues, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const isValid = Object.values(errors).every((error) => !error);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!stateTax || !isValid || !isDirty) return;

    const payload: UpdateStateTaxPayload = {
      oil_tax_percentage: parseFloat(formValues.oilTaxPercentage),
      gas_tax_percentage: parseFloat(formValues.gasTaxPercentage),
      ngl_tax_percentage: parseFloat(formValues.nglTaxPercentage),
      ad_valorem_tax_percentage: parseFloat(formValues.adValoremTaxPercentage),
    };

    updateStateTax(
      stateTax.id.toString(),
      payload,
      onSaved,
      setUpdateFailedError
    );
  };

  return (
    <Modal
      open={open}
      modalHeading={
        stateTax
          ? `Edit ${stateTax.stateAbbreviation} Taxes`
          : 'Edit State Taxes'
      }
      primaryButtonText="Save"
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleSubmit}
      preventCloseOnClickOutside
    >
      {updateFailedError && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle="Failed to update state tax. Please try again."
        />
      )}

      <TextInput
        id="stateAbbreviation"
        name="stateAbbreviation"
        labelText="State"
        readOnly
        value={formValues.stateAbbreviation}
        onChange={handleInputChange}
      />

      <div className={styles.inputWrapper}>
        <TextInput
          id="oilTaxPercentage"
          name="oilTaxPercentage"
          labelText="Oil Tax (%)"
          type="number"
          step="0.01"
          value={formValues.oilTaxPercentage}
          onChange={handleInputChange}
          invalid={!!errors.oilTaxPercentage}
          invalidText={errors.oilTaxPercentage}
        />
      </div>

      <div className={styles.inputWrapper}>
        <TextInput
          id="gasTaxPercentage"
          name="gasTaxPercentage"
          labelText="Gas Tax (%)"
          type="number"
          step="0.01"
          value={formValues.gasTaxPercentage}
          onChange={handleInputChange}
          invalid={!!errors.gasTaxPercentage}
          invalidText={errors.gasTaxPercentage}
        />
      </div>

      <div className={styles.inputWrapper}>
        <TextInput
          id="nglTaxPercentage"
          name="nglTaxPercentage"
          labelText="NGL Tax (%)"
          type="number"
          step="0.01"
          value={formValues.nglTaxPercentage}
          onChange={handleInputChange}
          invalid={!!errors.nglTaxPercentage}
          invalidText={errors.nglTaxPercentage}
        />
      </div>

      <div className={styles.inputWrapper}>
        <TextInput
          id="adValoremTaxPercentage"
          name="adValoremTaxPercentage"
          labelText="Ad Valorem Tax (%)"
          type="number"
          step="0.01"
          value={formValues.adValoremTaxPercentage}
          onChange={handleInputChange}
          invalid={!!errors.adValoremTaxPercentage}
          invalidText={errors.adValoremTaxPercentage}
        />
      </div>
    </Modal>
  );
};
